import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { UserContext } from "../../CheckoutPage";
import { useToast } from "../../../../contexts/ToastContext";
import {
  FinancialData,
  ItemDetailsData,
  RecommendedItem,
  PastOrderCustomerData, // Add interface for past order data
} from "../../../../Utils/DefinitionsInterfaces";
import {
  doNFOTransactionResponse,
  getCouponDiscount,
  getPastOrderCustomerData, // Add API for past order data
  doNFOTransaction,
  doNFOTransactionRazorpay,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import {
  BooksImage,
  Laptop,
  NFAImage,
  ReportImage,
  SecureCheckoutIcons,
} from "../../../../Assets/svgs";
import { formatDateTime } from "../../../../Utils/TimeConverter";
import {
  convertDate,
  ExamDate,
  processExamDates,
  filterUpcomingExamDates,
} from "../../../../Utils/helper";
// Add ReactGA for advanced analytics tracking
import ReactGA from "react-ga4";
// Add Sentry for error tracking
import * as Sentry from "@sentry/react";
// Add attribution tracking
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import "./V2PurchaseDetails.css";
import { useLocation, useNavigate } from "react-router-dom";

interface V2PurchaseDetailsProps {
  stateDefaultItems: FinancialData[];
  stateCheckoutItems: ItemDetailsData[];
  stateRecommendedItems: RecommendedItem[];
  cbRemoveProduct: (itemId: number) => void;
  cbAddRecommended: (itemId: number) => void;
  cbHandlePaymentClick: (data?: doNFOTransactionResponse) => void;
}

const V2PurchaseDetails: React.FC<V2PurchaseDetailsProps> = (props) => {
  const userContext = useContext(UserContext);

  // Check if we should disable physical book experiment (rollNumber + autoApplyRollAsCoupon)
  const shouldDisablePhysicalBook = () => {
    const rollNumber = sessionStorage.getItem("rollNumber");
    const hasAutoApplyCoupon = props.stateDefaultItems.some(
      (item) => item.autoApplyRollAsCoupon === true
    );
    return rollNumber && hasAutoApplyCoupon;
  };
  const { showToast } = useToast();
  const [showExamDatesModal, setShowExamDatesModal] = useState(false);
  const [upcomingExamDates, setUpcomingExamDates] = useState<ExamDate[]>([]);
  const [isLoadingExamDates, setIsLoadingExamDates] = useState(false);
  const [examDatesError, setExamDatesError] = useState<string | null>(null);
  // Context is guaranteed to be non-null, so we can access it directly
  const userDetails = userContext.userDetails;
  const backendSchool = userContext.backendSchool;
  const individualReg = userContext.individualReg;
  const examDate = userContext.examDate;
  // Add OTP verification context for form validation
  const isOtpVerified = userContext.isOtpVerified;

  const [totalSellingPrice, setTotalSellingPrice] = useState<number>(0);
  const [totalBasePrice, setTotalBasePrice] = useState<number>(0);
  const [gst, setGst] = useState<number>(0);
  const [shippingCost, setShippingCost] = useState<number>(0);

  // URL parameter states for pricing

  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [couponState, setCouponState] = useState({
    code: "",
    isApplied: false,
    isLoading: false,
    discountAmount: 0,
    originalAmount: 0,
    finalAmount: 0,
    error: "",
  });

  // Ref to track if auto-apply has been attempted to prevent infinite loops
  const autoApplyAttempted = useRef(false);

  // Set initial pricing mode immediately on component mount
  const nfo2024Param = sessionStorage.getItem("nfo2024");
  const pfhParam = sessionStorage.getItem("pfh");

  // Set initial pricing based on URL parameters (show special pricing upfront)
  const [isNfo2024Mode, setIsNfo2024Mode] = useState(nfo2024Param === "true");
  const [isPfhMode, setIsPfhMode] = useState(pfhParam === "true");

  console.log(
    "🔍 Initial pricing mode - NFO2024 param:",
    nfo2024Param,
    "PFH param:",
    pfhParam
  );
  console.log(
    "🔍 Initial pricing mode SET - NFO2024:",
    nfo2024Param === "true",
    "PFH:",
    pfhParam === "true"
  );

  // Update pricing after OTP verification with cross-eligibility logic
  useEffect(() => {
    const pfhVerified = sessionStorage.getItem("pfhVerified") === "true";
    const nfo2024Verified =
      sessionStorage.getItem("nfo2024Verified") === "true";
    const nfo2024Param = sessionStorage.getItem("nfo2024");
    const pfhParam = sessionStorage.getItem("pfh");

    // Only adjust pricing if verification has happened
    const hasVerificationResults =
      sessionStorage.getItem("pfhVerified") !== null ||
      sessionStorage.getItem("nfo2024Verified") !== null;

    if (!hasVerificationResults) {
      return; // Don't adjust pricing on initial load
    }

    console.log(
      "🔍 Cross-eligibility check - PFH verified:",
      pfhVerified,
      "NFO2024 verified:",
      nfo2024Verified
    );

    // Cross-eligibility logic: Try requested discount first, then fallback to alternative
    if (pfhParam === "true") {
      // PFH was requested - try PFH first, then NFO2024 as fallback
      if (pfhVerified) {
        setIsPfhMode(true);
        setIsNfo2024Mode(false);
        console.log("✅ Using PFH pricing (requested and verified)");
      } else if (nfo2024Verified) {
        setIsPfhMode(false);
        setIsNfo2024Mode(true);
        console.log("✅ Using NFO2024 pricing (PFH fallback)");
      } else {
        setIsPfhMode(false);
        setIsNfo2024Mode(false);
        console.log("❌ No discounts available - using standard pricing");
      }
    } else if (nfo2024Param === "true") {
      // NFO2024 was requested - try NFO2024 first, then PFH as fallback
      if (nfo2024Verified) {
        setIsNfo2024Mode(true);
        setIsPfhMode(false);
        console.log("✅ Using NFO2024 pricing (requested and verified)");
      } else if (pfhVerified) {
        setIsNfo2024Mode(false);
        setIsPfhMode(true);
        console.log("✅ Using PFH pricing (NFO2024 fallback)");
      } else {
        setIsNfo2024Mode(false);
        setIsPfhMode(false);
        console.log("❌ No discounts available - using standard pricing");
      }
    }

    console.log(
      "Post-verification pricing - Final modes - PFH:",
      isPfhMode,
      "NFO2024:",
      isNfo2024Mode
    );
  }, [userContext.pfhVerified, userContext.nfo2024Verified]);

  // Show pricing change notifications after OTP verification
  useEffect(() => {
    // Only run this effect when verification states actually change
    // This prevents toasts from showing on initial load
    if (!userContext.pfhVerified && !userContext.nfo2024Verified) {
      return; // Don't show toasts if no verification has occurred
    }

    const pfhVerified = sessionStorage.getItem("pfhVerified") === "true";
    const nfo2024Verified =
      sessionStorage.getItem("nfo2024Verified") === "true";
    const nfo2024Param = sessionStorage.getItem("nfo2024");
    const pfhParam = sessionStorage.getItem("pfh");

    console.log(
      "Toast logic - pfhVerified:",
      pfhVerified,
      "nfo2024Verified:",
      nfo2024Verified,
      "pfhParam:",
      pfhParam,
      "nfo2024Param:",
      nfo2024Param
    );

    // Cross-eligibility toast messages
    if (pfhParam === "true") {
      if (pfhVerified) {
        showToast(
          "Congratulations! Your exclusive discount has been applied successfully.",
          "success"
        );
      } else if (nfo2024Verified) {
        showToast(
          "Congratulations! Your exclusive discount has been applied successfully.",
          "success"
        );
      } else {
        showToast(
          "Oops! Looks like this number isn't linked to a handbook order. Try entering the mobile number you used when purchasing your handbook to avail this discount.",
          "error"
        );
      }
    } else if (nfo2024Param === "true") {
      if (nfo2024Verified) {
        showToast(
          "We're glad to have you back for the National Finance Olympiad 2025! Your special discount has been successfully applied.",
          "success"
        );
      } else if (pfhVerified) {
        showToast(
          "Congratulations! Your exclusive discount has been applied successfully.",
          "success"
        );
      } else {
        showToast(
          "Hmm... we couldn't find this number in our NFO 2024 records. Try entering the mobile number you used to register last year and grab your discount.",
          "error"
        );
      }
    }
  }, [userContext.pfhVerified, userContext.nfo2024Verified, showToast]);

  // Payment loading state
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  // Validation message state
  const [showValidationMessage, setShowValidationMessage] = useState(false);

  // COD confirmation modal state
  const [showCodConfirmation, setShowCodConfirmation] = useState(false);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showCodConfirmation) {
        setShowCodConfirmation(false);
      }
      if (event.key === "Escape" && showExamDatesModal) {
        setShowExamDatesModal(false);
      }
    };

    if (showCodConfirmation || showExamDatesModal) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [showCodConfirmation, showExamDatesModal]);

  // Load and filter upcoming exam dates
  const loadUpcomingExamDates = async () => {
    setIsLoadingExamDates(true);
    setExamDatesError(null);

    try {
      const allExamDates = processExamDates();
      const upcomingDates = await filterUpcomingExamDates(allExamDates);
      setUpcomingExamDates(upcomingDates);
    } catch (error) {
      console.error("Error loading exam dates:", error);
      setExamDatesError("Failed to load exam dates");
      // Fallback to all dates if filtering fails
      setUpcomingExamDates(processExamDates());
    } finally {
      setIsLoadingExamDates(false);
    }
  };

  // Load exam dates when modal opens
  useEffect(() => {
    if (showExamDatesModal && upcomingExamDates.length === 0) {
      loadUpcomingExamDates();
    }
  }, [showExamDatesModal]);

  // Past Order Customer Data state
  const [pastOrderCustomerData, setPastOrderCustomerData] = useState<
    PastOrderCustomerData[]
  >([]);
  const [isLoadingPastOrders, setIsLoadingPastOrders] = useState(true);
  const [pastOrdersError, setPastOrdersError] = useState<string | null>(null);

  // Advanced Analytics Tracking - from original FormCluster
  const [trackedEvents, setTrackedEvents] = useState<Record<string, boolean>>(
    () => {
      const saved = sessionStorage.getItem("trackedPurchaseEvents");
      return saved
        ? JSON.parse(saved)
        : {
            coupon_applied: false,
            coupon_removed: false,
            purchase_details_viewed: false,
            continue_to_payment_clicked: false,
            total_amount_calculated: false,
          };
    }
  );

  // Save tracking data to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "trackedPurchaseEvents",
      JSON.stringify(trackedEvents)
    );
  }, [trackedEvents]);

  // Analytics tracking function - matching original FormCluster
  const trackPurchaseEvent = (eventName: string, eventData?: any) => {
    if (!trackedEvents[eventName]) {
      trackData(`5678_checkout_${eventName}`);
      ReactGA.event({
        category: "Purchase_Flow",
        action: `5678_checkout_${eventName}`,
        label: "V2_Purchase_Details",
        ...eventData,
      });
      setTrackedEvents((prev) => ({
        ...prev,
        [eventName]: true,
      }));
    }
  };

  // Debug: Track when default items change (API response changes)
  useEffect(() => {
    console.log("V2PurchaseDetails - Default items changed:", {
      defaultItems: props.stateDefaultItems.map((item) => ({
        name: item.name,
        selling_price: item.selling_price,
        base_price: item.base_price,
        shipping_charges: item.shipping_charges || "not available",
      })),
      timestamp: new Date().toISOString(),
      trigger: "API response update",
    });
  }, [props.stateDefaultItems]);

  // Calculate totals
  useEffect(() => {
    let sumSellingPrice = 0;
    let sumBasePrice = 0;
    let sumShippingCharges = 0;

    // Process default items (now includes shipping_charges)
    for (let i = 0; i < props.stateDefaultItems.length; i++) {
      const item = props.stateDefaultItems[i];

      // Use different pricing based on URL parameters and API verification
      console.log(
        "🔍 Pricing calculation - isPfhMode:",
        isPfhMode,
        "isNfo2024Mode:",
        isNfo2024Mode,
        "pfhPrice:",
        item.pfhPrice,
        "nfo2024Price:",
        item.nfo2024Price,
        "selling_price:",
        item.selling_price,
        "base_price:",
        item.base_price
      );

      if (isPfhMode && item.pfhPrice > 0) {
        // Use PFH pricing when pfh=true (initially or after verification)
        sumSellingPrice += item.pfhPrice;
        sumBasePrice += item.base_price; // Keep base price as reference
        console.log("✅ Using PFH pricing:", item.pfhPrice);
      } else if (isNfo2024Mode && item.nfo2024Price > 0) {
        // Use NFO2024 pricing when nfo2024=true (initially or after verification)
        sumSellingPrice += item.nfo2024Price;
        sumBasePrice += item.base_price; // Keep base price as reference
        console.log("✅ Using NFO2024 pricing:", item.nfo2024Price);
      } else {
        // Use default pricing (fallback when not eligible or no special pricing)
        sumSellingPrice += item.selling_price;
        sumBasePrice += item.base_price;
        console.log("✅ Using default pricing:", item.selling_price);
      }

      // Use shipping_charges from API if available, otherwise fallback to fixed amount
      if (item.shipping_charges !== undefined) {
        sumShippingCharges += item.shipping_charges || 0;
      }
    }

    // Process checkout items
    for (let i = 0; i < props.stateCheckoutItems.length; i++) {
      sumSellingPrice += props.stateCheckoutItems[i].selling_price;
      sumBasePrice += props.stateCheckoutItems[i].base_price;
      // Checkout items might also have shipping_charges
      if ("shipping_charges" in props.stateCheckoutItems[i]) {
        sumShippingCharges +=
          (props.stateCheckoutItems[i] as any).shipping_charges || 0;
      }
    }

    // Debug: Log when selling price changes
    console.log("V2PurchaseDetails - Selling price calculation:", {
      defaultItems: props.stateDefaultItems.map((item) => ({
        selling_price: item.selling_price,
        base_price: item.base_price,
        shipping_charges: item.shipping_charges || "not available",
      })),
      checkoutItems: props.stateCheckoutItems.map((item) => ({
        id: item.id,
        selling_price: item.selling_price,
        base_price: item.base_price,
        shipping_charges: (item as any).shipping_charges || "not available",
      })),
      totalSellingPrice: sumSellingPrice,
      totalBasePrice: sumBasePrice,
      totalShippingCharges: sumShippingCharges,
      timestamp: new Date().toISOString(),
      userDetails: {
        mobileNumber: userDetails.mobileNumber,
        isOtpVerified: userContext.isOtpVerified,
      },
    });

    // Use shipping charges from API if available, otherwise fallback to fixed amount
    const calculatedShipping = sumShippingCharges;

    setTotalSellingPrice(sumSellingPrice);
    setTotalBasePrice(sumBasePrice);

    setShippingCost(calculatedShipping);
  }, [
    props.stateDefaultItems,
    props.stateCheckoutItems,
    userDetails.mobileNumber,
    userContext.isOtpVerified,
    isPfhMode,
    isNfo2024Mode,
  ]);

  // Fetch past order customer data - matching original CartSubtitle implementation
  useEffect(() => {
    const fetchPastOrders = async () => {
      setIsLoadingPastOrders(true);
      setPastOrdersError(null);
      try {
        const response = await getPastOrderCustomerData();
        if (response.data.status === "Success") {
          setPastOrderCustomerData(response.data.data);
        } else {
          setPastOrdersError("Failed to fetch past orders.");
        }
      } catch (error: any) {
        setPastOrdersError("Failed to fetch past orders.");
        showToast("Failed to fetch past orders. Please try again.", "warning");
        console.log("Past orders fetch error:", error);
      } finally {
        setIsLoadingPastOrders(false);
      }
    };

    fetchPastOrders();
  }, []);

  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const orderId = urlParams.get("order_id");
  const itemIds = urlParams.get("item_ids");
  const schoolCode = urlParams.get("scl");

  // Get selected payment method to determine pricing
  const selectedPaymentMethod =
    (sessionStorage.getItem("selectedPaymentMethod") as "card" | "cod") ||
    "card";

  // Debug: Log payment method changes and pricing
  useEffect(() => {
    console.log("V2PurchaseDetails - Payment method changed:", {
      selectedPaymentMethod,
      totalSellingPrice,
      shippingCost,
      couponState: {
        isApplied: couponState.isApplied,
        finalAmount: couponState.finalAmount,
        originalAmount: couponState.originalAmount,
      },
      timestamp: new Date().toISOString(),
    });
  }, [selectedPaymentMethod, totalSellingPrice, shippingCost, couponState]);

  // Calculate pricing based on payment method
  let totalAmount: number;
  if (selectedPaymentMethod === "cod") {
    // For COD: Include GST and shipping charges
    totalAmount = couponState.isApplied
      ? couponState.finalAmount + shippingCost
      : totalSellingPrice + shippingCost;
  } else {
    // For prepaid: Only base price, no GST or shipping
    totalAmount = couponState.isApplied
      ? couponState.finalAmount
      : totalSellingPrice;
  }

  // COD charge (only for COD payments)

  const finalTotalAmount = totalAmount;

  // Advanced Analytics Tracking - after totalAmount calculation
  // Track when component mounts - purchase details viewed
  useEffect(() => {
    trackPurchaseEvent("purchase_details_viewed");
  }, []);

  // Track when total amount is calculated
  useEffect(() => {
    if (totalAmount > 0) {
      trackPurchaseEvent("total_amount_calculated", {
        value: totalAmount,
        currency: "INR",
      });
    }
  }, [totalAmount]);

  // Format exam date like the original component
  const getFormattedExamDate = () => {
    if (
      individualReg &&
      backendSchool.examDate &&
      backendSchool.examDate.length > 0
    ) {
      const examDateSplit = formatDateTime(backendSchool.examDate).split(",");
      if (examDateSplit && examDateSplit.length >= 2) {
        return {
          date: examDateSplit[0],
          time: examDateSplit[1].trim(),
        };
      }
    } else if (!individualReg && examDate) {
      const formattedDate = convertDate(examDate);
      return {
        date: formattedDate,
        time: "11:00 AM - 12:00 PM",
      };
    }
    // Fallback to default
    return {
      date: "9 September 2025",
      time: "11:00 AM - 12:00 PM",
    };
  };

  const examInfo = getFormattedExamDate();

  // Handle coupon application
  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      setCouponState((prev) => ({
        ...prev,
        error: "Please enter a coupon code",
      }));
      return;
    }

    setCouponState((prev) => ({ ...prev, isLoading: true, error: "" }));

    try {
      const currentAmount = totalSellingPrice;
      const response = await getCouponDiscount(
        couponInput.trim(),
        currentAmount,
        false // Manual coupon application, not auto-apply
      );

      if (response.status === "Success") {
        setCouponState({
          code: response.data.coupon_code,
          isApplied: true,
          isLoading: false,
          discountAmount: response.data.discount_amount,
          originalAmount: response.data.priceBeforeDiscount,
          finalAmount: response.data.priceAfterDiscount,
          error: "",
        });

        // Store in session storage
        sessionStorage.setItem("appliedCouponCode", response.data.coupon_code);
        sessionStorage.setItem(
          "couponDiscountAmount",
          response.data.discount_amount.toString()
        );
        sessionStorage.setItem(
          "originalAmount",
          response.data.priceBeforeDiscount.toString()
        );

        // Advanced Analytics Tracking for coupon application
        trackPurchaseEvent("coupon_applied", {
          coupon_code: response.data.coupon_code,
          discount_amount: response.data.discount_amount,
          original_amount: response.data.priceBeforeDiscount,
          final_amount: response.data.priceAfterDiscount,
        });

        // Show success toast
        showToast(
          `Coupon "${response.data.coupon_code}" applied successfully! You saved ₹${response.data.discount_amount}`,
          "success"
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Invalid coupon code";
      showToast(errorMessage, "error");
      setCouponState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    // Track coupon removal before clearing state
    trackPurchaseEvent("coupon_removed", {
      coupon_code: couponState.code,
      discount_amount: couponState.discountAmount,
    });

    setCouponState({
      code: "",
      isApplied: false,
      isLoading: false,
      discountAmount: 0,
      originalAmount: 0,
      finalAmount: 0,
      error: "",
    });
    setCouponInput("");

    // Clear session storage
    sessionStorage.removeItem("appliedCouponCode");
    sessionStorage.removeItem("couponDiscountAmount");
    sessionStorage.removeItem("originalAmount");

    // Show success toast
    showToast("Coupon removed successfully!", "info");
  };

  // Auto-apply coupon from URL parameter or rollNumber
  const autoApplyCouponFromUrl = useCallback(async () => {
    if (couponState.isApplied || autoApplyAttempted.current) {
      return; // Coupon already applied or already attempted
    }

    // Check if auto-apply is enabled for any default item
    const shouldAutoApply = props.stateDefaultItems.some(
      (item) => item.autoApplyRollAsCoupon === true
    );

    if (!shouldAutoApply) {
      console.log("Auto-apply coupon disabled for this registration");
      autoApplyAttempted.current = true; // Mark as attempted to prevent re-checking
      return;
    }

    // Try URL coupon first, then rollNumber as coupon
    const urlCouponCode = sessionStorage.getItem("urlCouponCode");
    const rollNumber = sessionStorage.getItem("rollNumber");
    const couponToApply = urlCouponCode || rollNumber;

    if (!couponToApply) {
      console.log("No coupon code or rollNumber available for auto-apply");
      autoApplyAttempted.current = true;
      return;
    }

    console.log(
      `Auto-applying coupon: ${couponToApply} (from ${
        urlCouponCode ? "URL" : "rollNumber"
      })`
    );
    autoApplyAttempted.current = true; // Mark as attempted immediately
    setCouponInput(couponToApply);
    setCouponState((prev) => ({ ...prev, isLoading: true, error: "" }));

    try {
      const currentAmount = totalSellingPrice;
      const response = await getCouponDiscount(
        couponToApply,
        currentAmount,
        true
      );

      if (response.status === "Success") {
        setCouponState({
          code: response.data.coupon_code,
          isApplied: true,
          isLoading: false,
          discountAmount: response.data.discount_amount,
          originalAmount: response.data.priceBeforeDiscount,
          finalAmount: response.data.priceAfterDiscount,
          error: "",
        });

        // Store in session storage
        sessionStorage.setItem("appliedCouponCode", response.data.coupon_code);
        sessionStorage.setItem(
          "couponDiscountAmount",
          response.data.discount_amount.toString()
        );
        sessionStorage.setItem(
          "originalAmount",
          response.data.priceBeforeDiscount.toString()
        );

        // Analytics tracking for auto-applied coupon
        trackPurchaseEvent("coupon_auto_applied", {
          coupon_code: response.data.coupon_code,
          discount_amount: response.data.discount_amount,
          original_amount: response.data.priceBeforeDiscount,
          final_amount: response.data.priceAfterDiscount,
          source: urlCouponCode ? "url_parameter" : "roll_number",
        });

        // Show success toast
        showToast(
          `Coupon "${response.data.coupon_code}" auto-applied! You saved ₹${response.data.discount_amount}`,
          "success"
        );

        // Remove the URL coupon from session storage to prevent re-application
        if (urlCouponCode) {
          sessionStorage.removeItem("urlCouponCode");
        }
      }
    } catch (error: any) {
      console.error("Auto-apply coupon failed:", error);
      setCouponState((prev) => ({
        ...prev,
        isLoading: false,
        error: "", // Don't show error for auto-apply failures
      }));

      // Track failed auto-application
      trackPurchaseEvent("coupon_auto_apply_failed", {
        coupon_code: couponToApply,
        error_message: error.response?.data?.message || "Unknown error",
        source: urlCouponCode ? "url_parameter" : "roll_number",
      });
    }
  }, [couponState.isApplied, props.stateDefaultItems, totalSellingPrice]);

  // Auto-apply coupon from URL parameter when component loads and items are available
  useEffect(() => {
    if (
      props.stateDefaultItems.length > 0 &&
      totalSellingPrice > 0 &&
      !couponState.isApplied &&
      !autoApplyAttempted.current
    ) {
      autoApplyCouponFromUrl();
    }
  }, [
    props.stateDefaultItems,
    totalSellingPrice,
    couponState.isApplied,
    autoApplyCouponFromUrl,
  ]);

  const navigate = useNavigate();

  // Check if form is valid
  const isFormValid = () => {
    const emailValidation =
      /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,})$/;

    // Helper to check if string field is valid (not empty or whitespace only)
    const isValidString = (value: string | undefined): boolean => {
      return value !== undefined && value.trim().length > 0;
    };

    // Base validation (always required) - using trim to handle whitespace
    const baseValid =
      isValidString(userDetails.name) &&
      isValidString(userDetails.email) &&
      emailValidation.test(userDetails.email.trim()) &&
      isValidString(userDetails.mobileNumber) &&
      userDetails.grade !== 0 &&
      isOtpVerified; // Add OTP verification check

    // Address validation (only when forms are visible)
    // Removed regex/length validation requirements for delivery address
    const addressValid = shouldDisablePhysicalBook()
      ? true
      : isValidString(userDetails.city) &&
        isValidString(userDetails.state) &&
        isValidString(userDetails.pincode);

    return baseValid && addressValid;
  };

  // Debug helper to identify validation issues
  const getValidationDebugInfo = () => {
    const emailValidation =
      /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,})$/;
    
    // Helper to check if string field is valid (not empty or whitespace only)
    const isValidString = (value: string | undefined): boolean => {
      return value !== undefined && value.trim().length > 0;
    };
    
    return {
      name: isValidString(userDetails.name),
      email: isValidString(userDetails.email),
      emailFormat: userDetails.email ? emailValidation.test(userDetails.email.trim()) : false,
      mobileNumber: isValidString(userDetails.mobileNumber),
      grade: userDetails.grade !== 0,
      isOtpVerified,
      shouldDisablePhysicalBook: shouldDisablePhysicalBook(),
      city: isValidString(userDetails.city),
      state: isValidString(userDetails.state),
      pincode: isValidString(userDetails.pincode),
      addressValid: shouldDisablePhysicalBook()
        ? true
        : (isValidString(userDetails.city) && isValidString(userDetails.state) && isValidString(userDetails.pincode)),
      isPaymentLoading,
      formValid: isFormValid(),
      buttonDisabled: !isFormValid() || isPaymentLoading,
    };
  };

  // Debug: Log validation status when it changes (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const debugInfo = getValidationDebugInfo();
      console.log("🔍 Button Validation Debug:", debugInfo);
      
      // Log specific issues
      if (!debugInfo.formValid && !debugInfo.isPaymentLoading) {
        const issues = [];
        if (!debugInfo.name) issues.push("❌ Name missing");
        if (!debugInfo.email) issues.push("❌ Email missing");
        if (debugInfo.email && !debugInfo.emailFormat) issues.push("❌ Email format invalid");
        if (!debugInfo.mobileNumber) issues.push("❌ Mobile number missing");
        if (!debugInfo.grade) issues.push("❌ Grade not selected (is 0)");
        if (!debugInfo.isOtpVerified) issues.push("❌ OTP not verified (MOST COMMON ISSUE)");
        if (!debugInfo.shouldDisablePhysicalBook && !debugInfo.addressValid) {
          issues.push("❌ Address incomplete (city, state, or pincode missing)");
        }
        console.log("🚫 Button disabled because:", issues);
      }
    }
  }, [
    userDetails.name,
    userDetails.email,
    userDetails.mobileNumber,
    userDetails.grade,
    userDetails.city,
    userDetails.state,
    userDetails.pincode,
    isOtpVerified,
    isPaymentLoading,
    props.stateDefaultItems, // For shouldDisablePhysicalBook
  ]);

  // Get missing fields for validation message
  const getMissingFields = (): string[] => {
    const missingFields: string[] = [];
    const emailValidation =
      /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,})$/;

    // Helper to check if string field is valid (not empty or whitespace only)
    const isValidString = (value: string | undefined): boolean => {
      return value !== undefined && value.trim().length > 0;
    };

    // Base fields (always required) - using trim to match isFormValid logic
    if (!isValidString(userDetails.name)) missingFields.push("Student Name");
    if (!isValidString(userDetails.email)) {
      missingFields.push("Email");
    } else if (!emailValidation.test(userDetails.email.trim())) {
      missingFields.push("Valid Email");
    }
    if (!isValidString(userDetails.mobileNumber)) missingFields.push("Mobile Number");
    if (userDetails.grade === 0) missingFields.push("Grade");
    if (!isOtpVerified) missingFields.push("Mobile Number Verification (OTP)");

    // Address fields (only when forms are visible)
    // Removed address and flatNo validation requirements
    if (!shouldDisablePhysicalBook()) {
      if (!isValidString(userDetails.city)) missingFields.push("City");
      if (!isValidString(userDetails.state)) missingFields.push("State");
      if (!isValidString(userDetails.pincode)) missingFields.push("Pincode");
    }

    return missingFields;
  };

  // Complete payment handling function - matching original Subtotal logic
  /**
   * Payment Flow:
   * 1. Validate form completion (including OTP verification)
   * 2. Track analytics event
   * 3. For international schools: direct navigation
   * 4. For domestic schools:
   *    - Razorpay: Call doNFOTransactionRazorpay API → Store session data → Navigate
   *    - BillDesk: Call doNFOTransaction API → Navigate
   *    - COD: Call create-selfcheckout-order-cod API → Navigate to success
   * 5. Pass transaction response to payment handler
   * 6. Handle errors with Sentry tracking
   */
  // Handle actual COD order creation after confirmation
  const handleCodOrderConfirmation = async () => {
    setIsPaymentLoading(true);
    setShowCodConfirmation(false);

    // Track registration event for COD
    trackData("5678_checkout_register");
    ReactGA.event({
      category: "5678_checkout_register",
      action: "5678_checkout_register",
      label: "5678_checkout_register",
      value: Math.round(finalTotalAmount * 100), // Convert to paise like original
    });

    const pfh = sessionStorage.getItem("pfh");

    try {
      const codResponse = await fetch(
        "https://api.nationalfinanceolympiad.com/nfo/create-selfcheckout-order-cod",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: userDetails.name,
            schoolID: null,
            grade: userDetails.grade,
            mobileNumber: userDetails.mobileNumber,
            email: userDetails.email,
            choosenItemIDs: [], // Empty for default NFO registration
            schoolName: userDetails.school,
            pincode: userDetails.pincode,
            state: userDetails.state,
            city: userDetails.city,
            pfh: pfh === "true" ? true : false,
            couponCode: couponState.isApplied ? couponState.code : undefined,
            physicalBookExperiment: false,
            // address:
            //   [userDetails.flatNo, userDetails.address]
            //     .filter(Boolean)
            //     .join(", ") || "Not provided",
            paymentType: "cod",
            irs: true,
            nfo2024:
              sessionStorage.getItem("nfo2024") === "true" ? true : false,
          }),
        }
      );

      if (codResponse.ok) {
        const codData = await codResponse.json();
        console.log("COD order created:", codData);
        console.log("COD order ID -->:", codData.data.order_id);

        // Attribution tracking for COD success after getting order_id
        trackData(
          "nfo_register_success",
          userDetails.name,
          userDetails.mobileNumber,
          {
            success: true,
            payment_type: "cod",
            sale: {
              product_type: "nfo_register_success",
              amount_inr: finalTotalAmount,
              order_id: codData.data.order_id || undefined,
            },
          }
        );

        navigate(`/PaymentSuccessful/?&scl=${schoolCode ?? ""}`);
      } else {
        throw new Error("Failed to create COD order");
      }
    } catch (error) {
      console.error("Error creating COD order:", error);
      showToast("Failed to create COD order. Please try again.", "error");
      setIsPaymentLoading(false);
      // You can add error handling here (show error message, etc.)
    }
  };

  const handleCompletePayment = async () => {
    if (!isFormValid()) {
      setShowValidationMessage(true);
      return;
    }

    setIsPaymentLoading(true);

    // Analytics tracking
    // Advanced Analytics Tracking for continue to payment
    trackPurchaseEvent("continue_to_payment_clicked", {
      total_amount: finalTotalAmount,
      coupon_applied: couponState.isApplied,
      coupon_code: couponState.code,
      form_valid: isFormValid(),
      payment_method: selectedPaymentMethod,
    });

    // Attribution tracking for purchase button click
    trackData(
      "user_clicks_on_purchase_button",
      userDetails.name,
      userDetails.mobileNumber,
      {
        payment_type: selectedPaymentMethod === "cod" ? "cod" : "prepaid",
        sale: {
          product_type: "nfo_register_success",
          amount_inr: finalTotalAmount,
          order_id: undefined,
        },
      }
    );

    try {
      // Get selected payment method from session storage
      const selectedPaymentMethod =
        (sessionStorage.getItem("selectedPaymentMethod") as "card" | "cod") ||
        "card";

      // Handle COD payment - show confirmation modal first
      if (selectedPaymentMethod === "cod") {
        setShowCodConfirmation(true);
        setIsPaymentLoading(false);
        return;
      }

      // Attribution tracking for payment initiation
      trackData(
        "initiate_checkout",
        userDetails.name,
        userDetails.mobileNumber,
        {
          payment_type: "prepaid",
          sale: {
            product_type: "nfo_register_success",
            amount_inr: finalTotalAmount,
            order_id: undefined, // Will be set after API response
          },
        }
      );

      // Handle card payment (existing logic)
      // Prepare current item IDs - only from checkout items like original
      const currentItemIds: number[] = [];
      for (let i = 0; i < props.stateCheckoutItems.length; i++) {
        currentItemIds.push(props.stateCheckoutItems[i].id);
      }

      // // Ensure we have items to process
      // if (currentItemIds.length === 0) {
      //   alert("No items found in cart. Please refresh and try again.");
      //   setIsPaymentLoading(false);
      //   return;
      // }

      const referredBy = sessionStorage.getItem("referredBy");
      const pg = sessionStorage.getItem("pg");
      const pfh = sessionStorage.getItem("pfh");
      const channel =
        typeof window !== "undefined"
          ? sessionStorage.getItem("exp_name") || null
          : null;
      // Call new create-selfcheckout-order API for prepaid payments
      const prepaidResponse = await fetch(
        "https://api.nationalfinanceolympiad.com/nfo/create-selfcheckout-order",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: userDetails.name,
            schoolID: null,
            grade: userDetails.grade,
            mobileNumber: userDetails.mobileNumber,
            email: userDetails.email,
            choosenItemIDs: currentItemIds,
            schoolName: userDetails.school,
            pincode: userDetails.pincode,
            state: userDetails.state,
            city: userDetails.city,
            pfh: pfh === "true" ? true : false,
            couponCode: couponState.isApplied ? couponState.code : undefined,
            physicalBookExperiment: false,
            nfo2024:
              sessionStorage.getItem("nfo2024") === "true" ? true : false,
            referredBy: referredBy ? referredBy : null,
            // address:
            //   [userDetails.flatNo, userDetails.address]
            //     .filter(Boolean)
            //     .join(", ") || "Not provided",
            paymentType: "prepaid",
            irs: true,
            channel: channel,
          }),
        }
      );

      if (prepaidResponse.ok) {
        const prepaidData = await prepaidResponse.json();
        console.log("Prepaid data:", prepaidData);
        console.log("Prepaid order created:", prepaidData.message.amount / 100);

        // Check if the order was created successfully
        if (prepaidData.status === "Success") {
          // Attribution tracking for prepaid success after getting order_id
          trackData(
            "nfo_register_success",
            userDetails.name,
            userDetails.mobileNumber,
            {
              success: true,
              payment_type: "prepaid",
              sale: {
                product_type: "nfo_register_success",
                amount_inr: finalTotalAmount,
                order_id: prepaidData.message.id || undefined,
              },
            }
          );

          // Track registration event for card payment
          trackData("5678_checkout_register");
          ReactGA.event({
            category: "5678_checkout_register",
            action: "5678_checkout_register",
            label: "5678_checkout_register",
            value: Math.round(prepaidData.message.amount / 100), // Convert to paise like original
          });

          console.log("pg", pg);
          // For prepaid, we need to handle the payment gateway flow
          if (pg === "razorpay") {
            // Store the order data for Razorpay payment
            sessionStorage.setItem(
              "prepaidOrderData",
              JSON.stringify(prepaidData)
            );
            sessionStorage.setItem(
              "transactionAmount",
              JSON.stringify(prepaidData.message.amount / 100)
            );
            console.log(
              "transaction amount in session",
              prepaidData.message.amount / 100
            );

            // Ensure we have the order ID in the expected format
            const orderData = {
              ...prepaidData,
              orderID:
                prepaidData.orderID ||
                prepaidData.order_id ||
                (prepaidData.message && prepaidData.message.id),
              status: "Success",
            };

            console.log("Sending order data to payment handler:", orderData);
            console.log("Order ID extracted:", orderData.orderID);
            console.log(
              "Transaction amount stored:",
              prepaidData.message.amount / 100
            );

            // Navigate to payment
            props.cbHandlePaymentClick(orderData);
          } else {
            // For other payment gateways, handle accordingly
            props.cbHandlePaymentClick(prepaidData);
          }
        } else {
          throw new Error(prepaidData.message || "Order creation failed");
        }
      } else {
        const errorData = await prepaidResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to create prepaid order: ${prepaidResponse.status}`
        );
      }
    } catch (error: any) {
      console.error("Payment error:", error);

      // Sentry error tracking
      Sentry.captureException(
        "Error in V2 payment flow: " +
          (error.response?.data || error.message || error)
      );

      // Show toast notification instead of alert
      showToast("Payment failed. Please try again later.", "error");
    } finally {
      setIsPaymentLoading(false);
    }
  };

  return (
    <div className="v2-purchase-details">
      {/* Header */}
      <div className="v2-purchase-header">
        <h2 className="v2-purchase-title">Purchase Details</h2>
      </div>

      {/* <div className="v2-item-count">
        {props.stateDefaultItems.length + props.stateCheckoutItems.length} Item
        {props.stateDefaultItems.length + props.stateCheckoutItems.length !== 1
          ? "s"
          : ""}{" "}
        in Cart
      </div> */}

      {/* Main Product */}
      {/* {props.stateDefaultItems.map((item, index) => (
        <div key={index} className="v2-product-section">
          <div className="v2-product-header">
            <h3 className="v2-product-title">
              {item.name || "National Finance Olympiad & Study Material"}
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {item.base_price > item.selling_price ? (
                  <>
                    <div
                      style={{
                        textDecoration: "line-through",
                        fontSize: "18px",
                      }}
                      className="v2-product-price"
                    >
                      ₹{item.base_price}
                    </div>
                    <div className="v2-product-price">
                      ₹{item.selling_price}
                    </div>
                  </>
                ) : (
                  <div className="v2-product-price">₹{item.selling_price}</div>
                )}
              </div>
              {item.base_price > item.selling_price && (
                <div className="v2-product-discount">
                  {Math.round(
                    ((item.base_price - item.selling_price) / item.base_price) *
                      100
                  )}
                  % OFF
                </div>
              )}
            </div>
          </div>
          <p className="v2-product-description">
            {item.description ||
              item.subtitle ||
              "Includes study material & registration for the National Finance Olympiad"}
          </p>
        </div>
      ))} */}

      {/* Additional Checkout Items */}
      {props.stateCheckoutItems.map((item, index) => (
        <div key={`checkout-${index}`} className="v2-product-section">
          <div className="v2-product-header">
            <h3 className="v2-product-title">{item.name}</h3>
            <div className="v2-product-price">₹{item.selling_price}</div>
          </div>
          <p className="v2-product-description">
            {item.description || item.subtitle}
          </p>
        </div>
      ))}

      {/* Important Details */}
      <div className="v2-important-details">
        <h3 className="v2-details-title">Your Order Includes</h3>

        <div className="v2-detail-card">
          <img
            src={ReportImage}
            alt="Exam Schedule"
            className="v2-detail-image"
          />
          <div className="v2-detail-text">
            <div className="v2-exam-title-1">
              National Finance Olympiad Registration
            </div>
            {/* <div className="v2-exam-title">
              <span>Exam Dates: </span>
              <button
                className="v2-exam-dates-button"
                onClick={() => setShowExamDatesModal(true)}
              >
                View All Available Dates
              </button>
            </div> */}

            <div className="v2-exam-mode">
              Exam Mode: Proctored Online (from your home)
            </div>
          </div>
        </div>

        <div className="v2-detail-card">
          <img src={BooksImage} alt="Student Hub" className="v2-detail-image" />
          <div className="v2-detail-text">
            <div className="v2-hub-title">Olympiad Preparation Material</div>

            <div className="v2-hub-desc">
              1x - NFO Guidebook (E-Book)
              <br />
              1x - Sample Question Paper <br />
            </div>
          </div>
        </div>
        {userDetails.grade > 0 && userDetails.grade <= 8 ? (
          <div className="v2-detail-card">
            <img src={Laptop} alt="Student Hub" className="v2-detail-image" />
            <div className="v2-detail-text">
              <div className="v2-hub-title">Interactive Digital Activities</div>

              <div className="v2-hub-desc">
                The NFO Student Hub transforms important topics into interactive
                activities, helping students apply what they learn.
              </div>
            </div>
          </div>
        ) : (
          <div className="v2-detail-card">
            <img src={Laptop} alt="Student Hub" className="v2-detail-image" />
            <div className="v2-detail-text">
              <div className="v2-hub-title">Interactive Digital Quizzes</div>

              <div className="v2-hub-desc">
                The NFO Student Hub transforms important topics into interactive
                quizzes, helping students apply what they learn.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Coupon Section */}
      <div className="v2-coupon-section">
        {!couponState.isApplied ? (
          <div className="v2-coupon-input">
            <input
              type="text"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              className="v2-coupon-field"
              disabled={couponState.isApplied || couponState.isLoading}
            />
            <button
              onClick={handleApplyCoupon}
              disabled={couponState.isLoading || couponState.isApplied}
              className="v2-apply-coupon-btn"
            >
              {couponState.isLoading ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <div className="v2-loading-spinner"></div>
                  Applying...
                </div>
              ) : (
                "Apply"
              )}
            </button>
          </div>
        ) : (
          <div className="v2-coupon-success">
            <div className="v2-coupon-header">
              <div className="v2-success-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.5 6.1l-4.4 4.4c-.3.3-.8.3-1.1 0L3.5 8c-.3-.3-.3-.8 0-1.1s.8-.3 1.1 0L6 8.3l3.4-3.4c.3-.3.8-.3 1.1 0s.3.8 0 1.2z"
                    fill="#376CAA"
                  />
                </svg>
              </div>
              <span className="v2-success-text">
                Coupon <strong>"{couponState.code}"</strong> applied
                successfully!
              </span>
              <button
                onClick={handleRemoveCoupon}
                className="v2-remove-coupon"
                title="Remove coupon"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
                    fill="#666"
                  />
                </svg>
              </button>
            </div>
            <div className="v2-discount-row">
              <span className="v2-discount-label">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 0C3.1 0 0 3.1 0 7s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm3.5 7.5h-7v-1h7v1z"
                    fill="#376CAA"
                  />
                </svg>
                Discount:
              </span>
              <span className="v2-discount-value">
                -₹{couponState.discountAmount}
              </span>
            </div>
          </div>
        )}

        {/* Coupon error display */}
        {couponState.error && (
          <div className="v2-coupon-error">
            <div className="v2-error-content">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm4.3 10.7L10.7 12.3 8 9.6l-2.7 2.7L3.7 10.7 6.4 8 3.7 5.3 5.3 3.7 8 6.4l2.7-2.7 1.6 1.6L9.6 8l2.7 2.7z"
                  fill="#D32F2F"
                />
              </svg>
              <span>{couponState.error}</span>
            </div>
          </div>
        )}
      </div>

      {/* Pricing Breakdown */}
      <div className="v2-pricing-section">
        {/* Base Price */}
        <div className="v2-pricing-row">
          <span>Base Price</span>
          <span>₹{totalBasePrice}</span>
        </div>

        {/* Special Pricing Rows - Show the actual special prices being used */}
        {props.stateDefaultItems.map((item, index) => {
          if (
            isPfhMode &&
            item.pfhPrice > 0 &&
            item.pfhPrice !== item.selling_price
          ) {
            return (
              <div
                key={`pfh-${index}`}
                className="v2-pricing-row v2-special-pricing"
              >
                <span>PFH Special Price</span>
                <span>₹{item.pfhPrice}</span>
              </div>
            );
          }
          if (
            isNfo2024Mode &&
            item.nfo2024Price > 0 &&
            item.nfo2024Price !== item.selling_price
          ) {
            return (
              <div
                key={`nfo2024-${index}`}
                className="v2-pricing-row v2-special-pricing"
              >
                <span>NFO2024 Special Price</span>
                <span>₹{item.nfo2024Price}</span>
              </div>
            );
          }
          return null;
        })}

        {/* Website Discount (difference between base and selling price) */}
        {totalBasePrice > totalSellingPrice && (
          <div className="v2-pricing-row v2-discount-row">
            <span>
              {isPfhMode
                ? `PFH Discount (${Math.round(
                    ((totalBasePrice - totalSellingPrice) / totalBasePrice) *
                      100
                  )}% OFF)`
                : isNfo2024Mode
                ? `NFO2024 Discount (${Math.round(
                    ((totalBasePrice - totalSellingPrice) / totalBasePrice) *
                      100
                  )}% OFF)`
                : `Website Discount (${Math.round(
                    ((totalBasePrice - totalSellingPrice) / totalBasePrice) *
                      100
                  )}% OFF)`}
            </span>
            <span>-₹{totalBasePrice - totalSellingPrice}</span>
          </div>
        )}

        {/* Coupon Discount (if applied) */}
        {couponState.isApplied && (
          <div className="v2-pricing-row v2-discount-row">
            <span>Coupon Discount</span>
            <span>-₹{couponState.discountAmount}</span>
          </div>
        )}

        {/* Handling Charges (only for COD payments) */}
        {selectedPaymentMethod === "cod" && (
          <div className="v2-pricing-row">
            <span>Handling Charges</span>
            <span>+ ₹{shippingCost}</span>
          </div>
        )}
      </div>

      {/* Pricing explanation */}
      {/* {selectedPaymentMethod === "card" && (
        <div className="v2-pricing-note">
          <span>
            💡 Prepaid payments include all taxes and charges in the final
            amount
          </span>
        </div>
      )} */}

      {/* Total */}
      <div className="v2-total-section">
        <div className="v2-total-row">
          <span className="v2-total-label">Amount to be paid</span>
          <span className="v2-total-amount">₹{finalTotalAmount}</span>
        </div>
      </div>

      <button
        className={`v2-continue-btn ${
          !isFormValid() || isPaymentLoading ? "disabled" : ""
        }`}
        onClick={handleCompletePayment}
        disabled={!isFormValid() || isPaymentLoading}
      >
        {isPaymentLoading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <div className="v2-loading-spinner"></div>
            Processing...
          </div>
        ) : (
          "Continue to payment"
        )}
      </button>

      {/* Validation Message */}
      {showValidationMessage && !isFormValid() && (
        <div className="v2-validation-message">
          <div className="v2-validation-icon">⚠️</div>
          <div className="v2-validation-content">
            <div className="v2-validation-title">
              Please complete the following fields:
            </div>
            <div className="v2-validation-fields">
              {getMissingFields().map((field, index) => (
                <span key={index} className="v2-validation-field">
                  {field}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COD Confirmation Modal */}
      {showCodConfirmation && (
        <div
          className="v2-cod-modal-overlay"
          onClick={() => setShowCodConfirmation(false)}
        >
          <div className="v2-cod-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v2-cod-modal-header">
              <h3 style={{ fontWeight: "500" }}>Confirm Your Order</h3>
              <p>Please review your order details before confirming</p>
            </div>

            <div className="v2-cod-modal-content">
              {/* Product Details */}
              <div className="v2-cod-product-section">
                <h4 style={{ fontWeight: "500" }}>Product Details</h4>
                <div className="v2-cod-product-item">
                  <div className="v2-cod-product-info">
                    <span className="v2-cod-product-name">
                      National Finance Olympiad & Study Material
                    </span>
                    <span className="v2-cod-product-grade">
                      Grade {userDetails.grade}
                    </span>
                  </div>
                  <span className="v2-cod-product-price">
                    ₹{finalTotalAmount}
                  </span>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="v2-cod-price-section">
                <h4 style={{ fontWeight: "500" }}>Price Breakdown</h4>
                <div className="v2-cod-price-row">
                  <span>Base Price:</span>
                  <span>₹{totalBasePrice}</span>
                </div>

                {/* Website Discount */}
                {totalBasePrice > totalSellingPrice && (
                  <div className="v2-cod-price-row v2-cod-discount">
                    <span>
                      Website Discount (
                      {Math.round(
                        ((totalBasePrice - totalSellingPrice) /
                          totalBasePrice) *
                          100
                      )}
                      % OFF):
                    </span>
                    <span>-₹{totalBasePrice - totalSellingPrice}</span>
                  </div>
                )}

                {couponState.isApplied && (
                  <div className="v2-cod-price-row v2-cod-discount">
                    <span>Coupon Discount:</span>
                    <span>-₹{couponState.discountAmount}</span>
                  </div>
                )}

                {shippingCost > 0 && (
                  <div className="v2-cod-price-row">
                    <span>Handling Charges:</span>
                    <span>+ ₹{shippingCost}</span>
                  </div>
                )}

                <div className="v2-cod-price-row v2-cod-total">
                  <span>Total Amount:</span>
                  <span>₹{finalTotalAmount}</span>
                </div>
              </div>

              {/* Delivery Details */}
              <div className="v2-cod-delivery-section">
                <h4 style={{ fontWeight: "500" }}>Delivery Details</h4>
                <div className="v2-cod-delivery-info">
                  <p>
                    <strong>Name:</strong> {userDetails.name}
                  </p>
                  {(userDetails.flatNo || userDetails.address) && (
                    <p>
                      <strong>Address:</strong>{" "}
                      {[userDetails.flatNo, userDetails.address]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}
                  <p>
                    <strong>City:</strong> {userDetails.city},{" "}
                    {userDetails.state} - {userDetails.pincode}
                  </p>
                  <p>
                    <strong>Mobile:</strong> {userDetails.mobileNumber}
                  </p>
                  <p>
                    <strong>Payment:</strong> Cash on Delivery
                  </p>
                </div>
              </div>
            </div>

            <div className="v2-cod-modal-actions">
              <button
                className="v2-cod-cancel-btn"
                onClick={() => setShowCodConfirmation(false)}
                disabled={isPaymentLoading}
              >
                Cancel
              </button>
              <button
                className="v2-cod-confirm-btn"
                onClick={handleCodOrderConfirmation}
                disabled={isPaymentLoading}
              >
                {isPaymentLoading ? (
                  <div className="v2-cod-loading">
                    <div className="v2-loading-spinner"></div>
                    Processing...
                  </div>
                ) : (
                  "Confirm Order"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Icons */}
      {/* <div className="v2-security">
        <img
          src={SecureCheckoutIcons}
          alt="Secure Checkout and Payment Options"
          className="v2-security-image"
        />
      </div> */}

      {/* Past Order Social Proof - Commented out but available */}
      {/* Social proof section showing recent student registrations */}
      {/* {!isLoadingPastOrders && pastOrderCustomerData.length > 0 && (
        <div className="v2-social-proof-section">
          <div className="v2-social-proof-header">
            <h4>Recent Registrations</h4>
          </div>
          <div className="v2-testimonial-container">
            {pastOrderCustomerData
              .filter((item) => item.grade >= 6 && item.grade <= 12)
              .slice(0, 3)
              .map((item, index) => (
                <div key={index} className="v2-testimonial-item">
                  <div className="v2-testimonial-content">
                    <div className="v2-testimonial-text">
                      Student from {item.school_name} has registered for the
                      National Finance Olympiad
                    </div>
                    <div className="v2-testimonial-time">
                      <div className="v2-time-indicator"></div>
                      {item.minutes_ago < 60
                        ? `${item.minutes_ago} minutes ago`
                        : `${Math.floor(item.minutes_ago / 60)} hours ago`}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )} */}

      {/* Loading state for past orders */}
      {/* {isLoadingPastOrders && (
        <div className="v2-social-proof-loading">
          <div style={{ color: "#666", fontSize: "14px", textAlign: "center" }}>
            Loading recent registrations...
          </div>
        </div>
      )} */}

      {/* Error state for past orders */}
      {/* {pastOrdersError && (
        <div className="v2-social-proof-error">
          <div style={{ color: "#666", fontSize: "14px", textAlign: "center" }}>
            Unable to load recent registrations
          </div>
        </div>
      )} */}

      {/* Exam Dates Modal */}
      {showExamDatesModal && (
        <div
          className="v2-exam-dates-modal-overlay"
          onClick={() => setShowExamDatesModal(false)}
        >
          <div
            className="v2-exam-dates-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="v2-exam-dates-modal-header">
              <div className="v2-exam-dates-modal-title">
                <h3>Available Exam Dates</h3>
                <p className="v2-exam-dates-modal-subtitle">
                  You can select the date of exam in the student hub after
                  registration
                </p>
              </div>
              <button
                className="v2-exam-dates-modal-close"
                onClick={() => setShowExamDatesModal(false)}
              >
                ×
              </button>
            </div>
            <div className="v2-exam-dates-modal-content">
              {isLoadingExamDates ? (
                <div className="v2-exam-dates-loading">
                  <div className="v2-exam-dates-spinner"></div>
                  <p>Loading upcoming exam dates...</p>
                </div>
              ) : examDatesError ? (
                <div className="v2-exam-dates-error">
                  <p>⚠️ {examDatesError}</p>
                  <button
                    className="v2-exam-dates-retry"
                    onClick={loadUpcomingExamDates}
                  >
                    Retry
                  </button>
                </div>
              ) : upcomingExamDates.length === 0 ? (
                <div className="v2-exam-dates-empty">
                  <p>🎉 All exam dates for this session have passed!</p>
                  <p>Please check back for future exam schedules.</p>
                </div>
              ) : (
                <div className="v2-exam-dates-grid">
                  {upcomingExamDates.map((examDate, index) => (
                    <div key={index} className="v2-exam-date-item">
                      <div className="v2-exam-date">{examDate.date}</div>
                      <div className="v2-exam-time">{examDate.time}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default V2PurchaseDetails;
